import React, { useState, useEffect } from 'react';

/**
 * Inline Episode Counter component (- [input] +)
 * - Buttons fire onChange immediately
 * - Input field syncs on Enter or blur
 * - Capped at totalEpisodes (0 = unreleased, no progress allowed)
 */
const EpisodeCounter = ({ mediaId, currentProgress, totalEpisodes, isUpdating, onChange, label = 'episode' }) => {
    const [editing, setEditing] = useState(false);
    const [localValue, setLocalValue] = useState(currentProgress);

    // Sync local value when external progress changes
    useEffect(() => {
        if (!editing) {
            setLocalValue(currentProgress);
        }
    }, [currentProgress, editing]);

    const handleDecrement = (e) => {
        e.stopPropagation();
        if (currentProgress > 0) {
            onChange(currentProgress - 1);
        }
    };

    const handleIncrement = (e) => {
        e.stopPropagation();
        // Only increment if we haven't reached the cap
        if (totalEpisodes > 0 && currentProgress >= totalEpisodes) return;
        onChange(currentProgress + 1);
    };

    const handleInputChange = (e) => {
        const val = e.target.value;
        if (val === '' || /^\d+$/.test(val)) {
            setLocalValue(val === '' ? '' : parseInt(val, 10));
        }
    };

    const commitValue = () => {
        setEditing(false);
        const numVal = localValue === '' ? 0 : parseInt(localValue, 10);
        if (numVal !== currentProgress) {
            onChange(numVal);
        }
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter') {
            e.target.blur();
        }
        e.stopPropagation();
    };

    return (
        <div className="episode-counter" onClick={(e) => e.stopPropagation()}>
            <button
                className="ep-btn ep-dec"
                onClick={handleDecrement}
                disabled={isUpdating || currentProgress <= 0}
                title={`Decrease ${label}`}
            >
                −
            </button>
            <input
                type="text"
                className={`ep-input ${isUpdating ? 'updating' : ''}`}
                value={editing ? localValue : currentProgress}
                onFocus={(e) => { setEditing(true); setLocalValue(currentProgress); e.target.select(); }}
                onBlur={commitValue}
                onChange={handleInputChange}
                onKeyDown={handleKeyDown}
                disabled={isUpdating}
                title={`${currentProgress} / ${totalEpisodes} ${label}s`}
            />
            <button
                className="ep-btn ep-inc"
                onClick={handleIncrement}
                disabled={isUpdating || (totalEpisodes > 0 && currentProgress >= totalEpisodes) || totalEpisodes === 0}
                title={`Increase ${label}`}
            >
                +
            </button>
        </div>
    );
};

export default EpisodeCounter;
